import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Exam, type ExamState } from '../../exam.js';
import { StepState } from '../../extraction-progress.js';
import { evaluateMasterSolutionComplete } from '../../utils/solution-completeness.js';
import { hasReceivedAnySubmission } from '../../utils/exam-lock.js';
import { resolveIsStudentOwnedExam } from '../../utils/exam-role.js';
import { dispatchJobs } from '../jobs/dispatch.js';
import { GENERATION_JOB_TYPE } from '../jobs/job.js';
import { hasGradingSchemeContent, hasMasterSolutionContent } from '../agents/tree-generation-utils.js';

export const masterSolutionToolDef = [
  {
    type: 'function',
    function: {
      name: 'generate_master_solution',
      description:
        'Generiert KI-basierte Musterlösungen für die Aufgaben einer Klausur und ERGÄNZT dabei fehlende Teile: Aufgaben, die schon eine Musterlösung haben (auch aus einem Upload), bleiben unverändert — es werden nur die Aufgaben ohne Musterlösung erzeugt. Nutze dieses Tool also auch, wenn eine hochgeladene Musterlösung unvollständig ist und der Nutzer die fehlenden Teile ergänzen möchte. Rufe es nur auf, wenn der Nutzer danach fragt. Schülerabgaben werden dafür NICHT gebraucht: die Musterlösung wird aus den Aufgabenstellungen generiert, nicht aus Schülerantworten.',
      parameters: {
        type: 'object',
        properties: {
          examId: {
            type: 'string',
            description: 'Die ID der aktuellen Klausur',
          },
        },
        required: ['examId'],
      },
    },
  },
];

const collectGenerationTaskStats = (exam: any) => {
  let totalAnswerable = 0;
  let withMasterSolution = 0;
  let withGradingScheme = 0;

  const walk = (task: any) => {
    if (!task) return;

    const needsAnswer = task?.needsAnswer !== false;
    if (needsAnswer) {
      totalAnswerable += 1;

      // Same definition the generation agents use to decide what to target (see
      // tree-generation-utils.hasMasterSolutionContent): a bounding box counts, so an uploaded
      // image-only region is not reported as a gap the generation is about to fill.
      if (hasMasterSolutionContent(task)) withMasterSolution += 1;
      if (hasGradingSchemeContent(task)) withGradingScheme += 1;
    }

    if (Array.isArray(task?.subTasks)) {
      task.subTasks.forEach(walk);
    }
  };

  if (Array.isArray(exam?.tasks)) {
    exam.tasks.forEach(walk);
  }

  return { totalAnswerable, withMasterSolution, withGradingScheme };
};

const buildMasterSolutionToolStatusMessage = (exam: any): string => {
  const stats = collectGenerationTaskStats(exam);
  const taskExtractionStatus = exam?.examState?.taskExtractionProgress?.status;
  const msState = exam?.examState?.masterSolutionProgress;
  const msStatus = msState?.status;

  if (msStatus === 'ERROR') {
    return 'SYSTEM INFO: Bei der Erstellung der Musterlösung ist ein Fehler aufgetreten. Bitte versuche es erneut.';
  }

  if (msStatus === 'COMPLETED' || (stats.totalAnswerable > 0 && stats.withMasterSolution >= stats.totalAnswerable)) {
    return `SYSTEM INFO: Die Musterlösungen sind fertig (${stats.withMasterSolution}/${stats.totalAnswerable} Aufgaben) und in der Datenbank verfügbar. Schlage dem User vor mit der Erstellung des Bewertungsschemas zu starten oder die Musterlösungen zu überprüfen.`;
  }

  if (taskExtractionStatus !== 'COMPLETED') {
    return 'SYSTEM INFO: Die Erstellung der Musterlösungen wurde gestartet und wartet aktuell auf den Abschluss der Aufgabenextraktion. Teile dem User mit, dass ihm der Fortschritt in der Statusleiste am rechten Bildschirmrand angezeigt wird und schlage dem User vor mit der Erstellung des Bewertungsschemas fortzufahren.';
  }

  if (stats.totalAnswerable === 0) {
    return 'SYSTEM INFO: Die Erstellung der Musterlösungen wurde gestartet. Aktuell sind noch keine bewertbaren Aufgaben vorhanden. Teile dem User mit, dass ihm der Fortschritt der Musterlösungserstellung in der Statusleiste am rechten Bildschirmrand angezeigt wird und schlage dem User vor mit der Erstellung des Bewertungsschemas fortzufahren.';
  }

  // Some tasks already had a solution (typically from an incomplete upload) — this run only fills the
  // rest. Announced as completing, not as generating: the teacher's own material stays untouched, and
  // saying "a master solution is being generated" would suggest it gets replaced. Checked before the
  // IN_PROGRESS branch below, which is always true here because the caller just started the run.
  if (stats.withMasterSolution > 0 && stats.withMasterSolution < stats.totalAnswerable) {
    const missing = stats.totalAnswerable - stats.withMasterSolution;
    return `SYSTEM INFO: Die fehlenden Teile der Musterlösung werden jetzt im Hintergrund ergänzt (${missing} von ${stats.totalAnswerable} Aufgaben ohne Musterlösung). Die bereits vorhandenen Musterlösungen bleiben unverändert — sage dem User ausdrücklich, dass nur die fehlenden Teile ergänzt werden und nichts überschrieben wird. Der Fortschritt ist in der Statusleiste am rechten Bildschirmrand zu sehen.`;
  }

  if (msStatus === 'IN_PROGRESS' || (msState?.startedForTasks || []).length > 0) {
    return `SYSTEM INFO: Die Erstellung der Musterlösungen läuft (${stats.withMasterSolution}/${stats.totalAnswerable} Aufgaben fertig). Teile dem User mit, dass ihm der Fortschritt der Musterlösungserstellung in der Statusleiste am rechten Bildschirmrand angezeigt wird ist und schlage dem User vor mit der Erstellung des Bewertungsschemas fortzufahren.`;
  }

  return 'SYSTEM INFO: Die Erstellung der Musterlösungen wurde gestartet und läuft im Hintergrund. Teile dem User mit, dass ihm der Fortschritt der Musterlösungserstellung in der Statusleiste am rechten Bildschirmrand angezeigt wird und schlage dem User vor mit der Erstellung des Bewertungsschemas fortzufahren.';
};

export async function handleGenerateMasterSolution(exam: Exam, toolCallArgs: any): Promise<object> {
  const examIdToUse = toolCallArgs.examId || exam?._id?.toString();
  let toolResultContent = '';

  if (!examIdToUse) {
    toolResultContent = 'Fehler: Keine Klausur-ID.';
  } else {
    const currentExam = await (await getCollection(Exam)).findOne({ _id: examIdToUse });
    if (!currentExam) {
      toolResultContent = 'Fehler: Klausur nicht gefunden.';
    } else if ((await resolveIsStudentOwnedExam(examIdToUse)) && !hasReceivedAnySubmission(currentExam)) {
      // A student's own self-practice exam (created via create_exam for themselves, not a teacher grading a
      // class) — the point of generating a master solution from the task statements alone (see this tool's
      // own description) is exactly what would let the student see the answer key before they have finished
      // and handed in their own attempt. Blocked only for THIS case; a teacher's exam is never gated here —
      // see `resolveIsStudentOwnedExam`'s own doc comment on what it actually distinguishes.
      toolResultContent =
        'SYSTEM INFO: Abbruch. Dies ist die eigene Übungsklausur des Nutzers (Schüler-Account) und wurde noch nicht abgegeben — die Musterlösung bleibt bis dahin gesperrt, damit sie vor der eigenen Bearbeitung nicht eingesehen werden kann. Weise den Nutzer darauf hin, dass er die Klausur zuerst online ausfüllen und abgeben muss (oder seine bearbeitete Klausur hochladen), bevor die Musterlösung erstellt werden kann.';
    } else {
      // No precondition guard. `runMasterSolutionAgent` targets exactly the answerable tasks that have no
      // master solution yet (see its `!hasMasterSolutionContent` filter), so it fills the gaps of a
      // partially uploaded or partially generated solution and leaves the existing parts untouched. With
      // nothing to fill it is a no-op and the status message below reports that. Whether the tool is worth
      // offering at all is decided by `masterSolutionAvailability`, which asks the completeness check.
      const examCollection = await getCollection(Exam);
      // One call: it opens the run and enqueues one job per outstanding root, so the runner takes it from
      // there — roots in parallel, each retried on its own, and the whole thing surviving a restart, which the
      // previous detached agent call did not.
      //
      // Still synchronous, and for the same reason as before: a follow-up run_grading_agent in the same turn
      // has to see the source as started.
      await dispatchJobs(GENERATION_JOB_TYPE.masterSolution, examIdToUse);
      const refreshedExam = await examCollection.findOne({ _id: examIdToUse });
      toolResultContent = buildMasterSolutionToolStatusMessage(refreshedExam);
      logger.info({ toolResultContent }, 'Aktueller Status der Musterlösungserstellung nach Tool-Call');
    }
  }
  return { content: toolResultContent };
}

/**
 * Offered exactly while there is something to generate — the completeness check is the authority, the same
 * one that decides whether grading may start. Four consequences worth spelling out:
 *  - A complete master solution (uploaded, generated, or a mix) hides the tool, which replaces the old
 *    "a master solution already exists" refusal. That refusal also fired for an incomplete UPLOAD, which
 *    is precisely the case where the generation should step in.
 *  - No tasks means nothing to generate against, so the tool is not offered either.
 *  - While an uploaded master solution is still waiting to be extracted, its coverage is unknown: filling
 *    gaps now would write into tasks the extraction is about to fill and then be overwritten by it. An
 *    extraction that failed (ERROR) does not hold the tool back, otherwise it would be gone for good.
 *  - On a STUDENT's own self-practice exam (create_exam for themselves, not a teacher grading a class),
 *    hidden entirely until they have handed in their own attempt — see `hasReceivedAnySubmission`. This is
 *    a plain sync check against data already in hand (`role` is the session's, resolved once per turn by
 *    the caller — see `ToolAvailability`'s own doc comment), not the `resolveIsStudentOwnedExam` DB lookup
 *    `handleGenerateMasterSolution` still makes below: that one stays as the authoritative refusal for a
 *    caller that has no role to pass (defaults to offering the tool, per this function's own `!exam` rule),
 *    or for a stale tool list from before this exam's role could be resolved.
 */
export function masterSolutionAvailability(examState: ExamState, exam?: Exam, role?: 'teacher' | 'student'): boolean {
  if (!exam) return true;
  if (role === 'student' && !hasReceivedAnySubmission(exam)) return false;

  // "Was a file uploaded" is the id array itself — the old `masterSolutionUploaded` flag duplicated it and
  // a refine set it back to false. And with one state per artifact, "is that upload still waiting to be
  // read?" is read off `producer`: a state a GENERATION owns says nothing about the upload's extraction,
  // which by then has already run.
  const msProgress = examState?.masterSolutionProgress;
  const uploadWaitingForExtraction =
    Array.isArray((exam as any)?.masterSolutionIds) &&
    ((exam as any).masterSolutionIds as unknown[]).length > 0 &&
    // Neither a generation NOR a refine owns a state that says anything about the upload's extraction.
    msProgress?.producer !== 'generation' &&
    msProgress?.producer !== 'refine' &&
    (msProgress?.status === StepState.IN_PROGRESS ||
      msProgress?.status === StepState.NOT_STARTED ||
      !msProgress?.status);
  if (uploadWaitingForExtraction) return false;

  return !evaluateMasterSolutionComplete((exam as any).tasks).complete;
}
